import { useCallback, type CSSProperties, type ReactNode } from 'react'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import { cn } from '@/lib/utils'
import { HeroReveal, ScrollReveal, StaggerContainer, StaggerItem, TextReveal } from '@/components/motion'
import { FilterBar, HalftoneCover, ListingEmpty, ListingTagBox, useListingFilters, type FilterGroup, type ListingTone } from '@/components/landing/listing'
import { BRAND, OUT_EASE, halftone } from '@/components/landing/service/halftone'
import { HighlightHeading, PlatformCtaBand, PlatformFaq, TrialButton, hairline, ink, muted, quoteBar, type LayoutProps } from './shared'

/* ---------------------------- brand-asset tiles ---------------------------- */

type AssetType = 'Logos' | 'Colours' | 'Type' | 'Images'

interface AssetTile {
  id: string
  type: AssetType
  /** What the asset is, e.g. "Blue · #0C00FF". A description, not a file name. */
  caption: string
  /** Use-case industries (filter "Team") the asset belongs to. */
  teams: string[]
  /** Status chip drawn from the page's capabilities (versions, usage rights). */
  status: string
  art: ReactNode
}

function Wordmark({ reversed = false }: { reversed?: boolean }) {
  return (
    <span
      className={cn(
        'font-[family-name:var(--font-landing-subdisplay)] text-[clamp(1.25rem,9cqw,2.5rem)] font-bold tracking-[-0.03em]',
        reversed ? 'text-white' : 'text-[#0a0a0a]'
      )}
    >
      templ<span className={reversed ? 'text-[#ff00f2]' : 'text-[var(--color-landing-accent)]'}>a</span>tean
    </span>
  )
}

function Swatch({ hex, name, light = false }: { hex: string; name: string; light?: boolean }) {
  return (
    <span className="absolute inset-0 flex flex-col justify-end p-[7%]" style={{ backgroundColor: hex }}>
      <span className={cn('font-[family-name:var(--font-landing-display)] text-[clamp(0.9rem,7cqw,1.6rem)] uppercase leading-[1.3]', light ? 'text-[#0a0a0a]' : 'text-white')}>
        {name}
      </span>
      <span className={cn('mt-1 font-[family-name:var(--font-landing-mono)] text-[clamp(0.7rem,4.5cqw,0.95rem)]', light ? 'text-[#0a0a0a]/75' : 'text-white/80')}>
        {hex}
      </span>
    </span>
  )
}

function buildTiles(teams: string[]): AssetTile[] {
  const [marketing = 'Marketing', partners = marketing, operations = marketing] = teams
  const cover = (tone: ListingTone, angle: number, mark: ReactNode) => (
    <HalftoneCover tone={tone} angle={angle} aspect="auto" className="absolute inset-0">
      {mark}
    </HalftoneCover>
  )
  return [
    {
      id: 'logo-primary',
      type: 'Logos',
      caption: 'Primary logo',
      teams: [marketing, partners, operations],
      status: 'Approved',
      art: (
        <span className="absolute inset-0 flex items-center justify-center bg-white">
          <Wordmark />
        </span>
      ),
    },
    {
      id: 'colour-blue',
      type: 'Colours',
      caption: 'Primary blue',
      teams: [marketing, operations],
      status: 'Approved',
      art: <Swatch hex="#0C00FF" name="Blue" />,
    },
    {
      id: 'type-display',
      type: 'Type',
      caption: 'Display face · headlines',
      teams: [marketing],
      status: 'Licensed',
      art: (
        <span className="absolute inset-0 flex flex-col justify-between bg-[#f3f3f3] p-[8%] text-[#0a0a0a] dark:bg-[#15151d] dark:text-white">
          <span className="font-[family-name:var(--font-landing-display)] text-[clamp(2.5rem,30cqw,6rem)] leading-none">Aa</span>
          <span className="font-[family-name:var(--font-landing-display)] text-[clamp(0.6rem,4cqw,0.85rem)] uppercase leading-[1.5] tracking-wide">
            ABCDEFGHIJKLM
            <br />
            NOPQRSTUVWXYZ
          </span>
        </span>
      ),
    },
    {
      id: 'image-campaign',
      type: 'Images',
      caption: 'Campaign cover',
      teams: [marketing],
      status: 'v3 · current',
      art: cover(
        'blue',
        135,
        <span className="font-[family-name:var(--font-landing-display)] text-[clamp(2rem,22cqw,5rem)] uppercase leading-none text-white">T.</span>
      ),
    },
    {
      id: 'colour-purple',
      type: 'Colours',
      caption: 'Secondary purple',
      teams: [marketing, partners],
      status: 'Approved',
      art: <Swatch hex="#B200FF" name="Purple" />,
    },
    {
      id: 'logo-reversed',
      type: 'Logos',
      caption: 'Logo · reversed',
      teams: [marketing, partners],
      status: 'Partner access',
      art: (
        <span className="absolute inset-0 flex items-center justify-center bg-[#0a0a0a]">
          <Wordmark reversed />
        </span>
      ),
    },
    {
      id: 'image-partner',
      type: 'Images',
      caption: 'Co-marketing cover',
      teams: [partners],
      status: 'Partner access',
      art: cover(
        'pink',
        45,
        <span className="block h-[46%] w-[46%] bg-white shadow-[0.9rem_0.9rem_0_0_#0a0a0a]" />
      ),
    },
    {
      id: 'colour-pink',
      type: 'Colours',
      caption: 'Highlight pink',
      teams: [marketing],
      status: 'Approved',
      art: <Swatch hex="#FF00F2" name="Pink" light />,
    },
    {
      id: 'logo-mark',
      type: 'Logos',
      caption: 'Logo mark',
      teams: [marketing, partners, operations],
      status: 'v2 · current',
      art: (
        <span className="absolute inset-0 flex items-center justify-center bg-[#f3f3f3] dark:bg-[#15151d]">
          <span
            className="flex aspect-square w-[46%] items-center justify-center font-[family-name:var(--font-landing-display)] text-[clamp(1.5rem,16cqw,3.5rem)] leading-none text-white"
            style={{ backgroundColor: BRAND.blue, ...halftone(0.28, 6) }}
          >
            T
          </span>
        </span>
      ),
    },
    {
      id: 'type-body',
      type: 'Type',
      caption: 'Text face · body copy',
      teams: [marketing, operations],
      status: 'Licensed',
      art: (
        <span className="absolute inset-0 flex flex-col justify-between bg-white p-[8%] text-[#0a0a0a] dark:bg-[#1c1c26] dark:text-white">
          <span className="font-[family-name:var(--font-landing-subdisplay)] text-[clamp(2.5rem,30cqw,6rem)] font-semibold leading-none tracking-[-0.04em]">Aa</span>
          <span className="text-[clamp(0.65rem,4.2cqw,0.9rem)] leading-snug text-[#0a0a0a]/70 dark:text-white/70">
            The quick brown fox jumps over the lazy dog. 0123456789
          </span>
        </span>
      ),
    },
    {
      id: 'image-header',
      type: 'Images',
      caption: 'Document header',
      teams: [operations],
      status: 'v4 · current',
      art: cover(
        'purple',
        200,
        <span className="flex w-[70%] flex-col gap-[0.5rem] bg-white p-[8%]">
          <span className="block h-2 w-1/2 bg-[#0a0a0a]" />
          <span className="block h-1.5 w-full bg-[#e2e2e2]" />
          <span className="block h-1.5 w-4/5 bg-[#e2e2e2]" />
        </span>
      ),
    },
    {
      id: 'colour-ink',
      type: 'Colours',
      caption: 'Ink',
      teams: [marketing, partners, operations],
      status: 'Approved',
      art: <Swatch hex="#0A0A0A" name="Ink" />,
    },
  ]
}

const TYPE_ORDER: AssetType[] = ['Logos', 'Colours', 'Type', 'Images']
const COLLECTION_TONES: ListingTone[] = ['purple', 'blue', 'pink']

/**
 * Brand asset management ("asset-listing", works/blogs listing reference):
 * split header with a large rotated purple name box; a filterable brand-asset
 * grid (logos, swatches, type specimens, halftone covers) with the use cases
 * as collections; capabilities sticky-left / grid-right with the impact
 * figures; FAQ; blue CTA.
 */
export function DamLayout({ feature }: LayoutProps) {
  const reduce = useReducedMotion()
  const teams = feature.useCases.map((u) => u.industry)
  const tiles = buildTiles(teams)

  const groups: FilterGroup[] = [
    { id: 'type', label: 'Asset type', options: TYPE_ORDER.map((t) => ({ value: t, label: t })) },
    { id: 'team', label: 'Team', options: teams.map((t) => ({ value: t, label: t })) },
  ]
  const getValues = useCallback((tile: AssetTile, groupId: string) => (groupId === 'type' ? [tile.type] : tile.teams), [])
  const { selection, toggle, clear, filtered } = useListingFilters(tiles, getValues)

  const showTeam = (team: string) => {
    clear()
    toggle('team', team)
    document.getElementById('dam-assets')?.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' })
  }

  return (
    <>
      {/* 1. Split header */}
      <section aria-labelledby="dam-title" className="pt-16 md:pt-24 lg:pt-28">
        <div className="landing-container">
          <div className="grid gap-10 lg:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)] lg:items-end lg:gap-16">
            <div>
              <HeroReveal delay={0}>
                <p className="font-[family-name:var(--font-landing-display)] text-[clamp(2.75rem,6.6vw,8rem)] uppercase leading-[1.2] tracking-[-0.03em]">
                  <span className="inline-block -rotate-2 bg-[var(--color-landing-secondary)] px-[0.22em] pb-[0.06em] text-white">
                    {feature.shortLabel}.
                  </span>
                </p>
              </HeroReveal>
              <HeroReveal delay={0.1}>
                <h1 id="dam-title" className={cn('landing-text-display-xl mt-10', ink)}>
                  <span className="block">{feature.heroLines[0]}</span>{' '}
                  <span className="block">{feature.heroLines[1]}</span>
                </h1>
              </HeroReveal>
            </div>
            <HeroReveal delay={0.2} className="flex flex-col gap-8 lg:items-end lg:pb-3 lg:text-right">
              <p className={cn('landing-text-body-lg max-w-[42ch] text-pretty md:text-xl', muted)}>{feature.subheadline}</p>
              <TrialButton className="self-start lg:self-end" />
            </HeroReveal>
          </div>

          <HeroReveal delay={0.3}>
            <dl className={cn('mt-14 grid grid-cols-1 gap-6 border-t pt-8 sm:grid-cols-3 md:mt-20', hairline)}>
              {feature.heroStats.map((stat) => (
                <div key={stat.label} className="flex items-baseline gap-4">
                  <dd className="font-[family-name:var(--font-landing-display)] text-[clamp(1.75rem,3vw,2.75rem)] uppercase leading-[1.3] tracking-[-0.02em] text-[var(--color-landing-secondary)] dark:text-[#c94dff]">
                    {stat.value}
                  </dd>
                  <dt className={cn('text-base font-semibold', muted)}>{stat.label}</dt>
                </div>
              ))}
            </dl>
          </HeroReveal>
        </div>
      </section>

      {/* 2. Filterable brand-asset grid */}
      <section id="dam-assets" aria-labelledby="dam-assets-title" className="landing-section scroll-mt-24">
        <div className="landing-container">
          <h2 id="dam-assets-title" className="sr-only">
            Brand asset library
          </h2>
          <FilterBar
            groups={groups}
            selection={selection}
            onToggle={toggle}
            onClear={clear}
            resultLabel={`${filtered.length} of ${tiles.length} assets`}
          />
          {filtered.length === 0 ? (
            <ListingEmpty
              className="mt-10"
              title="No assets match"
              description="No asset in this library matches both filters. Clear one to see more."
              onReset={clear}
            />
          ) : (
            <motion.ul layout={!reduce} className="mt-10 grid grid-cols-2 gap-x-4 gap-y-8 md:grid-cols-3 md:gap-x-6 xl:grid-cols-4">
              <AnimatePresence mode="popLayout" initial={false}>
                {filtered.map((tile) => (
                  <motion.li
                    key={tile.id}
                    layout={!reduce}
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.96 }}
                    transition={{ duration: 0.35, ease: OUT_EASE }}
                    className="group"
                  >
                    <div
                      aria-hidden
                      className="relative aspect-[4/5] overflow-hidden outline outline-1 -outline-offset-1 outline-[var(--color-landing-gray-200)] [container-type:inline-size] dark:outline-white/10"
                    >
                      <div className="absolute inset-0 transition-transform duration-[600ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.06] motion-reduce:transition-none">
                        {tile.art}
                      </div>
                    </div>
                    <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                      <span className={cn('font-[family-name:var(--font-landing-subdisplay)] text-sm font-bold uppercase tracking-[0.01em] md:text-base', ink)}>
                        {tile.caption}
                      </span>
                      <span className="text-xs font-semibold uppercase tracking-wide text-[var(--color-landing-gray-500)]">{tile.status}</span>
                    </div>
                  </motion.li>
                ))}
              </AnimatePresence>
            </motion.ul>
          )}

          {/* Use cases as collections; each one filters the grid to its team */}
          <div className={cn('mt-20 grid gap-10 border-t pt-12 md:grid-cols-3 md:gap-8', hairline)}>
            {feature.useCases.map((useCase, i) => {
              const tone = COLLECTION_TONES[i % COLLECTION_TONES.length]!
              const count = tiles.filter((t) => t.teams.includes(useCase.industry)).length
              return (
                <ScrollReveal key={useCase.title} direction="none" delay={i * 0.08}>
                  <article className="flex h-full flex-col">
                    <ListingTagBox tag={{ label: useCase.industry, tone }} />
                    <h3 className={cn('mt-4 font-[family-name:var(--font-landing-display)] text-xl uppercase leading-[1.4] tracking-[-0.01em]', ink)}>
                      {useCase.title}
                    </h3>
                    <p className={cn('landing-text-body mt-3 max-w-[40ch]', muted)}>{useCase.description}</p>
                    <button
                      type="button"
                      onClick={() => showTeam(useCase.industry)}
                      className={cn(
                        'mt-auto self-start pt-6 font-[family-name:var(--font-landing-display)] text-sm uppercase underline decoration-1 underline-offset-[6px] transition-colors duration-200 hover:text-[var(--color-landing-accent)] focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[var(--color-landing-accent)]',
                        ink
                      )}
                    >
                      Show {count} assets
                    </button>
                  </article>
                </ScrollReveal>
              )
            })}
          </div>
        </div>
      </section>

      {/* 3. Capabilities: sticky left, grid right */}
      <section aria-labelledby="dam-capabilities-title" className="landing-section bg-[var(--color-landing-gray-50)] dark:bg-[#12121a]">
        <div className="landing-container grid items-start gap-14 lg:grid-cols-[minmax(0,0.4fr)_minmax(0,1fr)] lg:gap-20">
          <div className="lg:sticky lg:top-28">
            <HighlightHeading id="dam-capabilities-title" text={feature.valueProposition.title} color="purple" />
            <ScrollReveal direction="none" delay={0.15}>
              <p className={cn('landing-text-body-lg mt-8 max-w-[40ch]', quoteBar)}>{feature.valueProposition.description}</p>
            </ScrollReveal>
          </div>

          <div>
            <StaggerContainer amount={0.05} className="grid gap-x-6 gap-y-12 sm:grid-cols-2">
              {feature.capabilities.map((cap, i) => {
                const Icon = cap.icon
                const color = [BRAND.purple, BRAND.blue, BRAND.pink, BRAND.blue][i % 4]
                return (
                  <StaggerItem key={cap.title}>
                    <div
                      aria-hidden
                      className="flex aspect-[16/9] items-end justify-start p-6 text-white"
                      style={{ backgroundColor: color, ...halftone(0.3, 7) } as CSSProperties}
                    >
                      <Icon className="h-14 w-14" strokeWidth={1.25} />
                    </div>
                    <h3 className={cn('mt-5 font-[family-name:var(--font-landing-subdisplay)] text-xl font-bold uppercase tracking-[-0.01em]', ink)}>
                      {cap.title}
                    </h3>
                    <p className={cn('landing-text-body-sm mt-2 max-w-[44ch]', muted)}>{cap.description}</p>
                  </StaggerItem>
                )
              })}
            </StaggerContainer>

            <TextReveal as="h2" className={cn('landing-text-display-lg mt-24', ink)}>
              Business impact.
            </TextReveal>
            <ul className={cn('mt-8 border-t', hairline)}>
              {feature.benefits.map((b) => (
                <li key={b.title} className={cn('grid gap-2 border-b py-6 sm:grid-cols-[10rem_minmax(0,1fr)] sm:gap-8', hairline)}>
                  <p className="font-[family-name:var(--font-landing-display)] text-[2rem] uppercase leading-[1.3] text-[var(--color-landing-secondary)] dark:text-[#c94dff]">
                    {b.stat}
                  </p>
                  <div>
                    <h3 className={cn('text-lg font-semibold', ink)}>{b.title}</h3>
                    <p className={cn('landing-text-body-sm mt-1', muted)}>{b.description}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <PlatformFaq feature={feature} />
      <PlatformCtaBand feature={feature} tone="blue" />
    </>
  )
}
